/** Auth endpoints — login via email + OTP, returns JWT. */

import { request } from './client'

export interface RequestOtpReq {
  email: string
  device_fingerprint: string
}

export interface RequestOtpResp {
  status: string
  message: string
  ttl_minutes: number
}

export async function requestOtp(req: RequestOtpReq): Promise<RequestOtpResp> {
  return request<RequestOtpResp>('/api/auth/request-otp', {
    method: 'POST',
    body: req,
    noAuth: true,
  })
}

export interface VerifyOtpReq {
  email: string
  code: string
  device_fingerprint: string
}

export interface AuthUser {
  id: string
  email: string
  display_name?: string
  roles: string[]
  perms: string[]
}

/** Raw shape from `admin_service` — note `jwt` (not `token`). */
interface VerifyOtpRespRaw {
  jwt: string
  user: AuthUser
  trusted_device?: boolean
}

/** Normalised shape used internally — `token` is canonical. */
export interface VerifyOtpResp {
  token: string
  user: AuthUser
  trustedDevice: boolean
}

export async function verifyOtp(req: VerifyOtpReq): Promise<VerifyOtpResp> {
  const r = await request<VerifyOtpRespRaw>('/api/auth/verify-otp', {
    method: 'POST',
    body: req,
    noAuth: true,
  })
  return {
    token: r.jwt,
    user: r.user,
    trustedDevice: !!r.trusted_device,
  }
}

export async function me(): Promise<AuthUser> {
  return request<AuthUser>('/api/auth/me')
}

export async function logout(): Promise<void> {
  await request<unknown>('/api/auth/logout', { method: 'POST' })
}

/** Fingerprint is purely a label so the server can show "new device" in OTP emails.
 * No security claim — server treats it as opaque. We use UA + screen size hash. */
export function deviceFingerprint(): string {
  if (typeof window === 'undefined') return 'server'
  const ua = navigator.userAgent
  const screen = `${window.screen.width}x${window.screen.height}`
  const lang = navigator.language
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
  const raw = `${ua}|${screen}|${lang}|${tz}`
  // Cheap hash so the value isn't full UA in logs.
  let h = 0
  for (let i = 0; i < raw.length; i++) h = (h << 5) - h + raw.charCodeAt(i)
  return Math.abs(h).toString(16).padStart(8, '0')
}
